import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Wrench, ClipboardList, BarChart3, LogOut, Settings as SettingsIcon, Wallet, Menu, X, CalendarOff, ArrowLeft, Banknote, Calculator, Clapperboard, MessageSquare, Navigation, MessageCircle, Zap, TrendingUp, Bot, Route, PiggyBank, Plus, Minus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import TechReportPopup from "./TechReportPopup";
import AttendanceApprovalPopup from "./AttendanceApprovalPopup";
import TechArrivalPopup from "./TechArrivalPopup";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/orders", label: "Order", icon: ClipboardList, testid: "nav-orders" },
  { to: "/customers", label: "Pelanggan", icon: Users, testid: "nav-customers" },
  { to: "/leaves", label: "Libur Karyawan", icon: CalendarOff, testid: "nav-leaves" },
  { to: "/expenses", label: "Pengeluaran", icon: Wallet, testid: "nav-expenses" },
  { to: "/petty-cash", label: "Petty Cash", icon: PiggyBank, testid: "nav-petty-cash" },
  { to: "/uang-jalan", label: "Uang Jalan Tim", icon: Route, testid: "nav-uang-jalan" },
  { to: "/sosmed", label: "Content Sosmed", icon: Clapperboard, testid: "nav-sosmed" },
  { to: "/jarak-tempuh", label: "Jarak Tempuh", icon: Navigation, testid: "nav-mileage" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, end: true, testid: "nav-whatsapp" },
  { to: "/whatsapp/shortcuts", label: "Shortcut Pesan", icon: Zap, testid: "nav-wa-shortcuts", child: true },
  { to: "/pesan", label: "Pesan Karyawan", icon: MessageSquare, testid: "nav-pesan" },
  { to: "/reports", label: "Laporan", icon: BarChart3, testid: "nav-reports" },
  { to: "/settings", label: "Pengaturan", icon: SettingsIcon, end: true, testid: "nav-settings", toggle: "settings" },
  { to: "/settings/ai-agent", label: "AI Agent", icon: Bot, testid: "nav-ai-agent", child: true, group: "settings" },
  { to: "/technicians", label: "Karyawan", icon: Wrench, testid: "nav-technicians", child: true, group: "settings" },
  { to: "/kasbon", label: "Kasbon Karyawan", icon: Banknote, testid: "nav-kasbon", child: true, group: "settings" },
  { to: "/payroll", label: "Payroll", icon: Calculator, testid: "nav-payroll", child: true, group: "settings" },
  { to: "/laporan-keuangan", label: "Laporan Keuangan", icon: TrendingUp, testid: "nav-finance", child: true, group: "settings" },
];

const Layout = ({ children }) => {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  // Initial state pakai cached brand dari localStorage supaya tidak flicker saat navigasi antar halaman
  const [brand, setBrand] = useState(() => {
    try {
      const cached = localStorage.getItem("om_brand_cache");
      if (cached) return JSON.parse(cached);
    } catch (_) { /* noop */ }
    return { company_name: "", logo_base64: "" };
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sub menu Pengaturan bisa dibuka/tutup lewat tombol +/-; pilihan disimpan antar halaman.
  // Belum pernah dipilih → terbuka hanya bila sedang di salah satu sub menunya.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("om_nav_groups") || "{}"); } catch (_) { return {}; }
  });
  const isGroupOpen = (g) => {
    if (typeof openGroups[g] === "boolean") return openGroups[g];
    return items.some((it) => it.group === g && location.pathname.startsWith(it.to));
  };
  const toggleGroup = (g) => {
    const next = { ...openGroups, [g]: !isGroupOpen(g) };
    setOpenGroups(next);
    try { localStorage.setItem("om_nav_groups", JSON.stringify(next)); } catch (_) { /* noop */ }
  };

  // Set title immediately from cached brand supaya tidak flicker ORDER.OS ↔ nama company
  useEffect(() => {
    const name = (brand.company_name || "").trim();
    document.title = name || "ORDER.OS";
  }, [brand.company_name]);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const data = r.data || {};
      setBrand(data);
      try { localStorage.setItem("om_brand_cache", JSON.stringify(data)); } catch (_) { /* noop */ }
    }).catch(() => {});
  }, []);

  const [waUnread, setWaUnread] = useState(0);
  useEffect(() => {
    const fetchUnread = () => api.get("/wa/unread").then((r) => setWaUnread(r.data?.count || 0)).catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, []);

  // Simpan & pulihkan posisi scroll menu sidebar antar halaman
  const saveNavScroll = (e) => {
    try { sessionStorage.setItem("om_nav_scroll", String(e.currentTarget.scrollTop)); } catch (_) { /* noop */ }
  };
  const restoreNavScroll = (el) => {
    if (!el || el.dataset.restored) return;
    el.dataset.restored = "1";
    try { el.scrollTop = Number(sessionStorage.getItem("om_nav_scroll") || 0); } catch (_) { /* noop */ }
  };

  // Kunci scroll halaman saat drawer mobile terbuka
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const companyName = (brand.company_name || "").trim() || "ORDER.OS";
  const logo = (brand.logo_base64 || "").trim();

  const BrandBlock = () => (
    <div className="shrink-0 px-4 sm:px-6 py-5 border-b border-white/10" data-testid="sidebar-brand">
      {logo ? (
        <div className="flex items-center gap-3">
          <img src={logo} alt={companyName} className="h-10 w-10 object-contain bg-white p-1 shrink-0" data-testid="sidebar-brand-logo" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm font-black tracking-tight leading-tight line-clamp-2 break-words">{companyName}</div>
            <div className="label-cap text-white/50 text-[9px] mt-1">Manajemen Operasi</div>
          </div>
        </div>
      ) : (
        <>
          <div className="font-heading text-xl font-black tracking-tight leading-tight break-words">{companyName}</div>
          <div className="label-cap text-white/50 mt-1 text-[10px]">Manajemen Operasi</div>
        </>
      )}
    </div>
  );

  const NavList = ({ onClick }) => (
    <nav
      className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-4"
      style={{ WebkitOverflowScrolling: "touch" }}
      ref={restoreNavScroll}
      onScroll={saveNavScroll}
    >
      {items.filter((it) => !it.group || isGroupOpen(it.group)).map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={it.testid}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 ${it.child ? "pl-12 pr-6" : "px-6"} py-3 text-sm transition-colors min-h-[44px] ${
              isActive ? "bg-white text-[#111113] font-bold" : "text-white/80 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <it.icon size={it.child ? 14 : 16} /> {it.label}
          {it.to === "/whatsapp" && waUnread > 0 && (
            <span data-testid="nav-whatsapp-badge" className="ml-auto bg-[#16A34A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{waUnread}</span>
          )}
          {it.toggle && (
            <span
              role="button"
              tabIndex={0}
              aria-label={isGroupOpen(it.toggle) ? "Sembunyikan sub menu" : "Tampilkan sub menu"}
              aria-expanded={isGroupOpen(it.toggle)}
              data-testid="nav-settings-toggle"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroup(it.toggle); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggleGroup(it.toggle); } }}
              className="ml-auto -mr-2 p-1.5 border border-current/30 opacity-80 hover:opacity-100 hover:bg-black/10"
            >
              {isGroupOpen(it.toggle) ? <Minus size={12} /> : <Plus size={12} />}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  const Footer = () => (
    <div className="shrink-0 px-6 py-3 border-t border-white/10">
      <div className="text-xs text-white/50 mb-2 truncate" data-testid="sidebar-user-email">{user?.email}</div>
      <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-2 text-xs text-white/80 hover:text-white min-h-[44px]">
        <LogOut size={14} /> Keluar
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-[#E4E4E7] z-40 flex items-center px-3 gap-2" data-testid="mobile-topbar">
        <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-button" className="p-2 -ml-1 hover:bg-[#F4F4F5] shrink-0" aria-label="Buka menu">
          <Menu size={22} />
        </button>
        {logo ? (
          <img src={logo} alt={companyName} className="h-7 w-7 object-contain shrink-0" />
        ) : null}
        <div className="font-heading font-black text-sm tracking-tight truncate flex-1 min-w-0">{companyName}</div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-overlay" />
          <aside className="md:hidden fixed inset-y-0 left-0 h-[100dvh] max-h-[100dvh] w-[260px] max-w-[80vw] bg-[#111113] text-white z-50 flex flex-col overflow-hidden" data-testid="mobile-drawer">
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="label-cap text-white/50">Menu</div>
              <button onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-close" className="p-2 hover:bg-white/5"><X size={20} /></button>
            </div>
            {BrandBlock()}
            {NavList({ onClick: () => setDrawerOpen(false) })}
            {Footer()}
          </aside>
        </>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 bg-[#111113] text-white fixed inset-y-0 left-0 h-[100dvh] max-h-[100dvh] flex-col overflow-hidden z-30" data-testid="app-sidebar">
        {BrandBlock()}
        {NavList({})}
        {Footer()}
      </aside>

      {/* Main content */}
      <main className="md:ml-60 pt-14 md:pt-0 min-h-screen">
        {!isDashboard && (
          <div className="px-3 sm:px-6 lg:px-10 pt-3 sm:pt-6 lg:pt-10">
            <button
              onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
              data-testid="back-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:bg-white border border-[#E4E4E7] bg-white/60"
            >
              <ArrowLeft size={14} /> Kembali
            </button>
          </div>
        )}
        <div className={`p-3 sm:p-6 lg:p-10 ${!isDashboard ? "pt-3 sm:pt-4 lg:pt-6" : ""}`}>{children}</div>
      </main>
      <TechReportPopup />
      <AttendanceApprovalPopup />
      <TechArrivalPopup />
    </div>
  );
};

export default Layout;
